import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Modal, { ModalBody, ModalFooter } from './Modal';
import Button from './Button';

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const dayStart = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const sameDay = (a, b) =>
  Boolean(a && b) &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

/**
 * A month you page through, to pick a day or a range (Sam, 2026-10-12, option 1 of six): the two ends wear the selected
 * chip's glass, the days between sit on a soft band, the arrows are the map's round buttons. One tap is a day; a second
 * tap ends the range (an earlier day restarts it). Done with only a start is that one day. Our own grid, not
 * react-datepicker: the library's hover colours (a white square behind a tapped date) outranked the app's.
 */
export default function DateRangeSheet({ isOpen, onClose, value, onApply }) {
  const today = useMemo(() => dayStart(new Date()), [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [view, setView] = useState(() => monthStart(new Date()));
  useEffect(() => {
    if (!isOpen) return;
    const [s, e] = value ?? [null, null];
    setStart(s ? dayStart(s) : null);
    setEnd(e && s && !sameDay(s, e) ? dayStart(e) : null);
    setView(monthStart(s ?? new Date()));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (d) => {
    if (!start || end || d < start) {
      setStart(d);
      setEnd(null);
      return;
    }
    if (sameDay(d, start)) return;
    setEnd(d);
  };

  const cells = useMemo(() => {
    const first = monthStart(view);
    const days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const out = Array.from({ length: first.getDay() }, () => null);
    for (let i = 1; i <= days; i++) out.push(new Date(view.getFullYear(), view.getMonth(), i));
    return out;
  }, [view]);

  const atThisMonth =
    view.getFullYear() === today.getFullYear() && view.getMonth() === today.getMonth();
  const title = view.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const label = (d) =>
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Pick dates" variant="sheet" size="sm">
      <ModalBody className="pt-0">
        <div className="prii-cal">
          <div className="prii-cal__month">
            <button
              type="button"
              className="prii-cal__arrow"
              aria-label="Previous month"
              disabled={atThisMonth}
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <span className="prii-cal__title">{title}</span>
            <button
              type="button"
              className="prii-cal__arrow"
              aria-label="Next month"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
            >
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
          <div className="prii-cal__dow" aria-hidden="true">
            {DOW.map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>
          <div className="prii-cal__days" role="grid">
            {cells.map((d, i) => {
              if (!d) return <span key={`b${i}`} className="prii-cal__day prii-cal__day--blank" />;
              const past = d < today;
              const isStart = sameDay(d, start),
                isEnd = sameDay(d, end);
              const between = start && end && d > start && d < end;
              const band = end && (between || isStart || isEnd);
              const cls = [
                'prii-cal__day',
                past && 'prii-cal__day--past',
                sameDay(d, today) && 'prii-cal__day--today',
                band && 'prii-cal__day--band',
                band && isStart && 'prii-cal__day--band-start',
                band && isEnd && 'prii-cal__day--band-end',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <button
                  key={d.getTime()}
                  type="button"
                  className={cls}
                  disabled={past}
                  onClick={() => pick(d)}
                  aria-pressed={isStart || isEnd || Boolean(between)}
                  aria-label={label(d)}
                >
                  <span className={`prii-cal__num${isStart || isEnd ? ' prii-cal__num--end' : ''}`}>
                    {d.getDate()}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="prii-cal__caption">
            {start
              ? end
                ? `${label(start)} – ${label(end)}`
                : label(start)
              : 'Pick a day, or two for a range'}
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={() => {
            onApply([null, null]);
            onClose();
          }}
        >
          Clear
        </Button>
        <Button
          disabled={!start}
          onClick={() => {
            onApply([start, end ?? start]);
            onClose();
          }}
        >
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/** "Oct 14" or "Oct 14 – 18" (same month) or "Oct 30 – Nov 2". */
export function formatDateRangeShort(start, end) {
  if (!start) return null;
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (!end || sameDay(start, end)) return fmt(start);
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear())
    return `${fmt(start)} – ${end.getDate()}`;
  return `${fmt(start)} – ${fmt(end)}`;
}
